import React, { useMemo, useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import * as styles from './productInfo.module.scss'
import ic_safety from '../../../assets/images/ic_safety@2x.png'
import memoizeOne from 'memoize-one';
import _ from 'lodash'
import qrcode from '@images/qrcode-download-app.png';

const getAvailableSizes = memoizeOne((activeColor, activeWidth, colorSku, sizesSku, widthSku) => {
  if(activeColor || activeWidth) {
    const skuBycolor = colorSku[activeColor] || []
    const skuByWidth = widthSku[activeWidth] || []
    let size = []
    if(!activeColor) {
      size = skuByWidth
    }else if(!activeWidth) {
      size = skuBycolor
    } else {
      size =  _.intersectionBy(skuBycolor, skuByWidth, item => item.size?.name)
    }
    return size.reduce((init, item) => {
      const key = item.size?.name
      if(key) {
        init[key] = sizesSku[key] || []
      }
      return init
    }, {})
  }
})

const getAvailableColors = memoizeOne((activeSize, activeWidth, colorSku, sizesSku, widthSku) => {
  if(activeSize || activeWidth) {
    const skuBySize = sizesSku[activeSize] || []
    const skuByWidth = widthSku[activeWidth] || []
    let color = []
    if(!activeSize) {
      color = skuByWidth
    }else if(!activeWidth) {
      color = skuBySize
    } else {
      color =  _.intersectionBy(skuBySize, skuByWidth, item => item.color?.name)
    }
     return color.reduce((init, item) => {
      const key = item.color?.name
      if(key) {
        init[key] = colorSku[key] || []
      }
      return init
    }, {})
  }
})

const getAvailableWidth =  memoizeOne((activeColor, activeSize, colorSku, sizesSku, widthSku) => {
  if(activeSize || activeColor) {
    const skuBySize = sizesSku[activeSize] || []
    const skuBycolor = colorSku[activeColor] || []
    let width = []
    if(!activeSize) {
      width = skuBycolor
    }else if(!activeColor) {
      width = skuBySize
    } else {
      width =  _.intersectionBy(skuBySize, skuBycolor, item => item.attributes?.attributes?.width?.choices?.[0])
    }
    return width.reduce((init, item) => {
      const key = item.attributes?.attributes?.width?.choices?.[0]
      if(key) {
        init[key] = widthSku[key] || []
      }
      return init
    }, {}) 
  }
})

const ProductInfo = ({ info }) => {

  const { 
    brand, nameCN, name, labelView, colors = [], sizes = [], availableSkus = [], attributes 
  } = info

  const widthList = useMemo(() => {
    return attributes?.attributes?.width?.choices || []
  }, [attributes])

  const [
    {
      activeColor, 
      activeSize,
      activeWidth
    }, setActiveSku 
  ] = useState({
    activeColor: '',
    activeSize: '',
    activeWidth: ''
  })

  const [price, setPrice] = useState({
    priceTagCN: '',
    priceTag: '',
    originalPriceTag: ''
  })

  useEffect(() => {
    const {priceTag, priceTagCN, originalPriceTag} = (info.promotions || {})
    setPrice({
      priceTag,
      priceTagCN,
      originalPriceTag
    })
  }, [info.promotions])

  const { colorSku, sizesSku, widthSku } = useMemo(() => {
    return availableSkus.reduce((initival, item) => {
      const color = item.color?.name
      const size = item.size?.name
      const width = item.attributes?.attributes?.width?.choices?.[0]
      if(color) {
        if(!Array.isArray(initival.colorSku[color])) {
          initival.colorSku[color] = []
        }
        initival.colorSku[color].push(item)
      }
      if(size) {
        if(!Array.isArray(initival.sizesSku[size])) {
          initival.sizesSku[size] = []
        }
        initival.sizesSku[size].push(item)
      }
      if(width) {
        if(!Array.isArray(initival.widthSku[width])) {
          initival.widthSku[width] = []
        }
        initival.widthSku[width].push(item)
      }
      return initival
    }, {colorSku: {}, sizesSku: {}, widthSku: {}})
  }, [availableSkus])

  const { availableColors, availableSizes, availableWidth } = useMemo(() => {
    const availableSizes = getAvailableSizes(activeColor, activeWidth, colorSku, sizesSku, widthSku)
    const availableColors = getAvailableColors(activeSize, activeWidth, colorSku, sizesSku, widthSku)
    const availableWidth = getAvailableWidth(activeColor, activeSize, colorSku, sizesSku, widthSku)
    return {
      availableColors: availableColors || colorSku,
      availableSizes: availableSizes || sizesSku,
      availableWidth: availableWidth || widthSku
    }
  }, [colorSku, sizesSku, activeColor, activeSize, activeWidth, widthSku])

  const getProductTitle = (language = 'en') => {
    return `${brand ? brand + ' | ' : ''}${ language === 'en' ? name : (nameCN || name)}`
  }

  const labelViews = useMemo(() => {
    const shortLabels = (labelView && labelView.shortLabels) || []
    return shortLabels.reduce((init, item ) => {
      return [...init, ...(item.labels || []).filter((label) => label.text)]
    }, [])
  }, [labelView])

  const onSelectColor = (color) => {
    setActiveSku(pre => ({
      ...pre,
      activeColor: pre.activeColor === color ? '' : color
    }))
  }

  const onSelectSize = (size) => {
    setActiveSku(pre => ({
      ...pre,
      activeSize: pre.activeSize === size ? '' : size
    }))
  }

  const onSelectWidth = (width) => {
    setActiveSku(pre => ({
      ...pre,
      activeWidth: pre.activeWidth === width ? '' : width
    }))
  }

    const labelViewsEl = useRef()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if(labelViews.length) {
      const children = labelViewsEl.current.children
      setVisible(!!Math.floor(children[children.length - 1].offsetTop))
    }
  }, [labelViews])

  useEffect(() => {
    if(!availableSkus?.length) {
      return
    }
    const { priceTag, priceTagCN, originalPriceTag,  skus = []  } = info.promotions || {}
    if(!activeColor && colors.length) {
      setPrice({
        priceTag,
        priceTagCN,
        originalPriceTag
      })
      return 
    }
    if(!activeSize && sizes.length) {
      setPrice({
        priceTag,
        priceTagCN,
        originalPriceTag
      })
      return
    }
    if(!activeWidth && widthList.length) {
      setPrice({
        priceTag,
        priceTagCN,
        originalPriceTag
      })
      return
    }
    const isAccordWithSku = (sku) => {
      if(activeColor) {
        if(sku?.color?.name !== activeColor) { return false }
      }
      if(activeSize) {
        if(sku?.size?.name !== activeSize) { return false }
      }
      if(activeWidth) {
        if(sku.attributes?.attributes?.width?.choices?.[0] !== activeWidth) { return false }
      }
      return true
    }
    const sku = availableSkus.find(sku => isAccordWithSku(sku))
    if(sku && skus) {
      const target = skus.find(item => item.id === sku.id || item.id === '_all') || {}
      setPrice({
        priceTag: target.priceTag,
        priceTagCN: target.priceTagCN,
        originalPriceTag: target.originalPriceTag
      })
    }
  }, [
    activeColor, activeSize, activeWidth, colors, sizes, availableSkus, widthList, info.promotions ])

  const features = info?.featureBar?.features || [];
  return (
    <ul className={styles.infoWrapper}>
      <li className={styles.title}>
        { getProductTitle('zh') }
      </li>
      <li className={styles.subtitle}>
        { getProductTitle('en') }
      </li>
      <li className={styles.price}>
        <span className={styles.label}>价格</span>
        <span className={styles.priceTagCn}>{ price.priceTagCN }</span>
        <span className={styles.priceTag}>{ price.priceTag }</span>
        <span className={styles.originalPriceTag}>{ price.originalPriceTag }</span>
      </li>
      {
        labelViews.length ? (
          <li>
            <span className={styles.label}>促销</span>
            <div  className={`${styles.labelView} ${visible ? styles.visible : ''}`}>
              <div className={styles.labelItemGroup} ref={labelViewsEl}>
                {
                  labelViews.map(({text, color, backgroundColor}) => (
                    <div 
                      className={styles.labelItem}
                      style={{
                        color : `#${color}`,
                        backgroundColor: `#${backgroundColor}`
                      }}>
                      { text }
                    </div>
                  ))
                }
              </div>
            </div>
          </li>
        ): null
      }
      {
        colors.length ? (
          <li className={`${styles.colors} ${styles.property}`}>
            <span className={styles.label}>颜色</span>
            <div className={styles.colorsMain}>
              {
                colors.map(item => (
                  <div 
                    className={`
                      ${availableColors[item.name] ? '' : styles.unavailable}
                      ${activeColor === item.name ? styles.active : ''}
                    `}
                    key={item.name}
                    onClick={() => availableColors[item.name] && onSelectColor(item.name)}
                  >
                    <img src={item.image?.thumbnail?.url || item.image.full.url} alt="color thumbnail" />
                  </div>
                ))
              }
            </div>
          </li>
        ): null
      }
      {
        widthList.length ? (
          <li className={`${styles.sizes} ${styles.property}`}>
            <span className={styles.label}>宽度</span>
            <div className={styles.sizesMain}>
              {
                widthList.map(item => (
                  <div 
                    className={`
                      ${availableWidth[item] ? '' : styles.unavailable}
                      ${activeWidth === item ? styles.active : ''}
                    `}
                    key={item}
                    onClick={() => availableWidth[item] && onSelectWidth(item)}
                  >
                    { item }
                  </div>
                ))
              }
            </div>
          </li>
        ): null
      }
      {
        sizes.length ? (
          <li className={`${styles.sizes} ${styles.property}`}>
            <span className={styles.label}>尺码</span>
            <div className={styles.sizesMain}>
              {
                sizes.map(item => (
                  <div 
                    className={`
                      ${availableSizes[item.name] ? '' : styles.unavailable}
                      ${activeSize === item.name ? styles.active : ''}
                    `}
                    key={item.name}
                    onClick={() => availableSizes[item.name] && onSelectSize(item.name)}
                  >
                    {item.label || item.name}
                  </div>
                ))
              }
            </div>
          </li>
        ): null
      }
      {/* {
        !hideModule?.hide && (
          <li className={styles.commitment}>
            <span className={styles.label}>承诺</span>
            <div className={styles.commitmentGroup}>
              <div>
                <img src={ic_safety} alt="safety" />
                <span>别样严格审核</span>
              </div>
              <div>
                <img src={ic_safety} alt="safety" />
                <span>签约保障协议</span>
              </div>
              <div>
                <img src={ic_safety} alt="safety" />
                <span>支持官方鉴定</span>
              </div>
            </div>
          </li>
        )
      } */}
      {
        features.length ? (
          <li className={styles.service}>
            <span className={styles.label}>服务</span>
            <div className={styles.serviceGroup}>
              {
                features.map(item => <div key={item.name}>{ item.name }</div>)
              }
            </div>
          </li>
        ) : null
      }
      <li className={styles.qrWrap}>
        <img src={qrcode} alt="QR" />
        <div>
          <div>扫码下单，</div>
          <div>
            领<span>¥688</span>大礼包～
          </div>
        </div>
      </li>
    </ul>
  )
}

ProductInfo.displayName = "ProductInfo"
ProductInfo.propTypes = {
  info: PropTypes.object
}
ProductInfo.defaultProps = {
  info: {}
}

export default ProductInfo